import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Iconify } from '@repo/design-system/components/ui';
import './SocialMediaLinks.scss';

export const getSocialPlatformIcon = (platformName = '', variant = 'brand') => {
    const lower = platformName.toLowerCase();
    if (variant === 'color') {
        switch (lower) {
            case 'instagram':
                return 'skill-icons:instagram';
            case 'linkedin':
                return 'logos:linkedin-icon';
            case 'youtube':
                return 'logos:youtube-icon';
            case 'facebook':
                return 'logos:facebook';
            case 'github':
                return 'logos:github-icon';
            case 'twitter':
            case 'x':
                return 'ri:twitter-x-fill';
            default:
                return 'solar:global-bold';
        }
    }

    switch (lower) {
        case 'instagram':
            return 'mdi:instagram';
        case 'linkedin':
            return 'ri:linkedin-box-fill';
        case 'youtube':
            return 'mdi:youtube';
        case 'facebook':
            return 'ri:facebook-box-fill';
        case 'github':
            return 'mdi:github';
        case 'twitter':
        case 'x':
            return 'ri:twitter-x-fill';
        default:
            return 'solar:global-bold';
    }
};

export const normalizeSocialLinks = (socialData, socialLinks, variant = 'brand') => {
    if (Array.isArray(socialData) && socialData.length > 0) {
        return socialData
            .map((item, idx) => {
                if (!item) return null;
                const platformName = item?.name || (typeof item === 'object' ? Object.keys(item)[0] : '');
                const rawUrl = item?.url || (platformName ? item[platformName] : '');
                if (!platformName || !rawUrl) return null;
                const targetUrl = rawUrl.startsWith('http://') || rawUrl.startsWith('https://') ? rawUrl : `https://${rawUrl}`;
                return {
                    id: idx,
                    name: platformName,
                    url: targetUrl,
                    icon: getSocialPlatformIcon(platformName, variant),
                    platformKey: platformName.toLowerCase()
                };
            })
            .filter(Boolean);
    }

    if (socialLinks && typeof socialLinks === 'object') {
        return Object.entries(socialLinks)
            .filter(([_, url]) => Boolean(url))
            .map(([platform, url], idx) => {
                const targetUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
                return {
                    id: idx,
                    name: platform.charAt(0).toUpperCase() + platform.slice(1),
                    url: targetUrl,
                    icon: getSocialPlatformIcon(platform, variant),
                    platformKey: platform.toLowerCase()
                };
            });
    }

    return [];
};

export const SocialMediaLinks = ({
    socialNetwork,
    socialLinks,
    iconVariant = 'brand',
    iconSize = 22,
    showEmptyMessage = false,
    emptyMessage = 'No social links available',
    className = '',
    onLinkClick
}) => {
    const items = useMemo(() => {
        return normalizeSocialLinks(socialNetwork, socialLinks, iconVariant);
    }, [socialNetwork, socialLinks, iconVariant]);

    if (items.length === 0) {
        if (showEmptyMessage) {
            return (
                <div className={`social-media-empty ${className}`.trim()}>
                    <Iconify icon="solar:link-broken-minimalistic-linear" width={16} height={16} className="empty-icon" />
                    <span>{emptyMessage}</span>
                </div>
            );
        }
        return null;
    }

    const handleClick = (e, item) => {
        if (onLinkClick) {
            onLinkClick(item, e);
        }
    };

    return (
        <div className={`social-media-links ${className}`.trim()}>
            {items.map((item) => (
                <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`social-media-link-item social-${item.platformKey}`}
                    title={item.name}
                    aria-label={item.name}
                    onClick={(e) => handleClick(e, item)}
                >
                    <Iconify icon={item.icon} width={iconSize} height={iconSize} />
                </a>
            ))}
        </div>
    );
};

SocialMediaLinks.propTypes = {
    socialNetwork: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    socialLinks: PropTypes.object,
    iconVariant: PropTypes.oneOf(['brand', 'color']),
    iconSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showEmptyMessage: PropTypes.bool,
    emptyMessage: PropTypes.string,
    className: PropTypes.string,
    onLinkClick: PropTypes.func
};
